import { useEffect, useState } from 'react';

import {
    Box,
    Button,
    Card,
    Divider,
    Flex,
    Heading,
    Icon,
    Link,
    List,
    ListItem,
    Text,
    UnorderedList,
    useBreakpointValue
} from '@chakra-ui/react';

import moment from 'moment';
import 'moment-timezone';

import { DonationDisplay } from './Misc/DonationDisplay';
import { SetupBox } from './Misc/SetupBox';

import { FaUserEdit, FaMusic } from "react-icons/fa";
import { FaRegCalendar, FaRegCalendarPlus } from "react-icons/fa6";
import { FiExternalLink } from "react-icons/fi";
import { TbLocation } from "react-icons/tb";
import { RiInfoCardLine } from "react-icons/ri";

export function EventInfo({eventConfig, sessionActive, changePage, setups, regTotal, regAllowed, schedAllowed, outputAllowed, marAllowed}) {

    const [SetupGrid, setSetupGrid] = useState([]);
    const [eventStartDayStr, setEventStartDayStr] = useState("");
    const [eventEndDayStr, setEventEndDayStr] = useState("");
    const [eventYear, setEventYear] = useState("");
    const [eventTimeItems, setEventTimeItems] = useState([]);

    const eventNum = eventConfig["eventNum"];
    const eventShort = eventConfig["eventShort"];
    const liveLong = eventConfig["liveLong"];
    const eventOver = eventConfig["eventOver"];

    const regButtonShown = !eventOver && sessionActive && (regAllowed);
    const schedOutputButtonShown = !eventOver && sessionActive && outputAllowed;
    const schedMarButtonShown = !eventOver && sessionActive && marAllowed;

    const isMobile = useBreakpointValue(
        {
          "base": ()=> true,
          "xl": ()=>  false
        }
    );

    const SignUpDisplay = () => {
        if (!eventOver && (!sessionActive || regButtonShown || schedOutputButtonShown)) {
            return (
                <Card padding={isMobile()? 3 : 8} mb={4}>   
                    <Heading size="md" mb={4}>
                        How do I sign up for {liveLong}?
                    </Heading>
                    <Divider mb={4}/>                                     
                    <SchedButtonsText/>
                    <Box textAlign="center">
                        <RegisterButton/>
                        <ScheduleOutputButton/>
                        <ScheduleMarButton/>
                    </Box>
                </Card>
            );
        }
        return ([]);
    }

    const SchedButtonsText = () => {
        if(sessionActive) {
            if(regButtonShown) {
                return (
                    <Card bgColor="whiteAlpha.300" padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            Registration
                        </Heading>
                        <Divider mb={4}/>
                        <Text mb={4}>
                            If you want to attend, you need to register.  Once you do, you can schedule a set if you'd like to compete!
                        </Text>
                    </Card>
                );
            }
            if(schedOutputButtonShown) {
                let schedText = "";

                if (outputAllowed && !marAllowed) { 
                    schedText = "Now that you've registered, you can schedule your Raw Output.  After that, you can schedule your Full Marathon set if your team is doing one.";
                } else if (outputAllowed && marAllowed) {
                    schedText = "You can schedule a Raw Output or Full Marathon for the teams you belong to now that you've registered.";
                } else if (!outputAllowed && marAllowed) {
                    schedText = "Now that you've scheduled your Raw Output, you can schedule your Full Marathon set if your team is doing one.";
                }

                return (
                    <Card bgColor="whiteAlpha.300" padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            Raw Output Scheduling
                        </Heading>
                        <Divider mb={4}/>
                        <Text mb={4}>
                            {schedText}
                        </Text>
                    </Card>
                );
            }            
            return([]);
        }
        return (
            <Card bgColor="whiteAlpha.300" padding={isMobile()? 3 : 8} mb={4}>
                <Heading size="md" mb={4}>
                    Registration
                </Heading>
                <Divider mb={4}/>
                <Text mb={4}>
                    In order to sign up to attend, you'll need to do the following:
                </Text>
                <UnorderedList ml={isMobile() ? 8 : 12} mb={4}>
                    <ListItem><Link color='teal.300' href={`https://groovestats.com/index.php?page=register`} isExternal>Register for a GrooveStats account <Icon as={FiExternalLink} mx='2px' top='3px' position="relative"/></Link></ListItem>
                    <ListItem><Link color='teal.300' onClick={()=>{changePage("log-in")}}>Log in on this website</Link></ListItem>
                    <ListItem>You can then sign up to buy a badge and come to compete or just hang out</ListItem>
                </UnorderedList>
            </Card>
        );
    };

    const RegisterButton = () => {
        if(regButtonShown) {
            return (
                <Button width={{base: "250px", xl: "600px"}} isDisabled={!regAllowed} flex="1" onClick={()=>{changePage("register")}}>
                    <Icon as={FaUserEdit} boxSize={5} mr={2}/> Register For Event
                </Button>
            );
        }
        return([]);
    }

    const ScheduleOutputButton = () => {
        if(schedOutputButtonShown) {
            return (
                <Button width={{base: "250px", xl: "600px"}} isDisabled={!schedAllowed} flex="1" onClick={()=>{changePage("sched-output")}}>
                    <Icon as={FaRegCalendarPlus} boxSize={5} mr={2}/> Schedule Raw Output
                </Button>
            );
        }
        return([]);
    }    

    const ScheduleMarButton = () => {
        if(schedMarButtonShown) {
            return (
                <Button width={{base: "250px", xl: "600px"}} isDisabled={!schedAllowed} flex="1" onClick={()=>{changePage("sched-mar")}}>
                    <Icon as={FaRegCalendarPlus} boxSize={5} mr={2}/> Schedule Full Marathon
                </Button>
            );
        }
        return([]);
    }        

    useEffect(()=>{
        const newSetupGrid = Object.values(setups)?.map((setupId, idx)=>
            <SetupBox
                setups={setupId} 
                key={idx}
            />
        );
        setSetupGrid(newSetupGrid);
    }, [setups]);

    useEffect(()=>{
        const eventDates = eventConfig?.eventDates;

        const newEventTimes = [];

        eventDates.forEach((eventDate, idx)=>{
            const startDateTime = moment.tz(`${eventDate["date"]} ${eventDate["start"]}`, "America/New_York");
            const endDateTime = moment.tz(`${eventDate["date"]} ${eventDate["end"]}`, "America/New_York");

            const startWeekday = startDateTime.format("dddd");

            const startTime = startDateTime.format("h:mm a");
            const endTime = endDateTime.format("h:mm a");

            if (idx === 0) {
                const newEventStartDayStr = startDateTime.format("MMMM Do");
                const newEventYear = startDateTime.format("YYYY");
                setEventStartDayStr(newEventStartDayStr);
                setEventYear(newEventYear);
            }

            if (idx === (eventDates.length -1)) {
                const newEventEndDayStr = endDateTime.format("MMMM Do");
                setEventEndDayStr(newEventEndDayStr);
            }

            newEventTimes.push({
                startWeekday: startWeekday,
                startTime: startTime,
                endTime: endTime
            })
        });

        const newTimeListItems = newEventTimes?.map((time, idx)=>
            <ListItem key={idx}><strong>{time.startWeekday}:</strong> {time.startTime} - {time.endTime}</ListItem>
        );

        setEventTimeItems(newTimeListItems);
    }, [eventConfig]);

    try {
        return (
            <Box width="100%">

                <Text mb={4}>
                    Join us for a fun, team-oriented weekend of dance game fun at the sequel to Stamina Jawn!
                </Text>
                <Flex flexFlow="wrap" justifyContent="center" flexDirection="row" gap={3}>
                    <Card flex="2" flexBasis="375px" padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            When is {liveLong}?
                        </Heading>
                        <Divider mb={4}/>
                        <Heading color="blue.200" size="md" mb={4}>
                            {eventStartDayStr}-{eventEndDayStr} {eventYear}
                        </Heading>
                        <Text mb={4}>
                            The space will open the following hours:
                        </Text>
                        <List>
                            {eventTimeItems}
                        </List>
                    </Card>
                    <Card flex="2" flexBasis="375px" padding={isMobile()? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            Where is {liveLong}?
                        </Heading>
                        <Divider mb={4}/>
                        <Heading color="blue.200" size="md" mb={4}>
                            PGH Dance Game Space<br/>
                            Pittsburgh, PA
                        </Heading>
                        <Text mb={4}>
                            You can find more information on the venue by visiting the venue info page:
                        </Text>
                        <Flex>
                            <Button flex="1" mb={4} onClick={()=>{changePage("venue-info")}}>
                                <Icon as={TbLocation} boxSize={5} mr={2}/> Venue Info
                            </Button>
                        </Flex>
                    </Card>
                </Flex>
                <Card padding={isMobile()? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        What is {liveLong}?
                    </Heading>
                    <Divider mb={4}/>
                    <Text mb={4}>
                        {liveLong} is an opportunity for stamina players to come together in person and do their East Coast 
                        Stamina {eventNum} sets live.  Since it's a team event, it's natural that you'd want to get together
                        to do it.
                    </Text>
                    <Text mb={4}>
                        Additionally, at {liveLong}, participating in those sets contributes points towards a ranking for 
                        the teams present at the event-- and you can also participate in <strong>side events</strong> that 
                        will contribute points as well!
                    </Text>                    
                    <Card bgColor="whiteAlpha.300" flex="2" flexBasis="375px" padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            Main Events:
                        </Heading>
                        <Divider mb={4}/>                             
                        <Text mb={4}>
                            Your team's point total for East Coast Stamina {eventNum} will contribute points towards their standing for {liveLong}.  Your team's 
                            combined performance on their Raw Output and Marathon portions determines the point
                            yields, which are as follows:
                        </Text>
                        <Flex fontSize="12px" flexWrap="wrap" ml="auto" mr="auto" alignContent="center" justifyContent="center" mb={2}>
                            <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                <Heading size="sm" mr="auto" mb={2} ml="auto">1 Point</Heading>
                                <UnorderedList ml={isMobile() ? 4 : 6}>
                                    <ListItem>Finishing both portions legitimately</ListItem>                                 
                                </UnorderedList>
                            </Card>   
                            <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                <Heading size="sm" mr="auto" mb={2} ml="auto">2 Points</Heading>
                                <UnorderedList ml={isMobile() ? 4 : 6}>
                                    <ListItem>1100+ points in Lower Division Single</ListItem>
                                    <ListItem>650+ points in Lower Division Double</ListItem>                                        
                                </UnorderedList>
                            </Card>      
                            <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                <Heading size="sm" mr="auto" mb={2} ml="auto">3 Points</Heading>
                                <UnorderedList ml={isMobile() ? 4 : 6}>
                                    <ListItem>1750+ points in Lower Division Single</ListItem>
                                    <ListItem>1000+ points in Lower Division Double</ListItem>                                        
                                    <ListItem>300+ points in Upper Division Single</ListItem>
                                    <ListItem>175+ points in Upper Division Double</ListItem>
                                </UnorderedList>
                            </Card>    
                            <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                <Heading size="sm" mr="auto" mb={2} ml="auto">4 Points</Heading>
                                <UnorderedList ml={isMobile() ? 4 : 6}>
                                    <ListItem>500+ points in Upper Division Single</ListItem>
                                    <ListItem>250+ points in Upper Division Double</ListItem>
                                </UnorderedList>
                            </Card>                            
                            <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                <Heading size="sm" mr="auto" mb={2} ml="auto">5 Points</Heading>
                                <UnorderedList ml={isMobile() ? 4 : 6}>
                                    <ListItem>1000+ points in Upper Division Single</ListItem>
                                    <ListItem>500+ points in Upper Division Double</ListItem>
                                </UnorderedList>
                            </Card>                                                                                           
                        </Flex>
                        <Text mb={4}>
                            You can read up on East Coast Stamina {eventNum} here:
                        </Text>
                        <Button 
                            ml="auto" 
                            mr="auto" 
                            mb={4} 
                            width={{base: "250px", xl: "400px"}} 
                            onClick={()=>{
                                window.open(`https://${eventShort}.groovestats.com/`, '_blank').focus();
                            }}    
                        >
                            <Icon as={RiInfoCardLine} boxSize={5} mr={2}/> ECS{eventNum} Info/Rules <Icon as={FiExternalLink} mx='2px' ml={2} top='1px' position="relative"/>
                        </Button>                                
                        <Text mb={4}>
                            ... and you can view the songlists and other relevant data for each division below:
                        </Text>
                        <Flex direction="column">
                            <Heading size="md">Lower Division:</Heading>
                            <Divider mb={4}/>
                            <Flex direction="row" wrap="wrap" alignSelf="center" gap={2}>
                                <Button 
                                    ml="auto" 
                                    mr="auto" 
                                    mb={4} 
                                    width={{base: "250px", xl: "400px"}} 
                                    onClick={()=>{
                                        window.open(`https://${eventShort}.groovestats.com/index.php?page=tourneysonglist&division=Lower`, '_blank').focus();
                                    }}    
                                >
                                    <Icon as={FaMusic} boxSize={5} mr={2}/> Raw Output Data <Icon as={FiExternalLink} mx='2px' ml={2} top='1px' position="relative"/>
                                </Button>
                                <Button 
                                    ml="auto" 
                                    mr="auto" 
                                    mb={4} 
                                    width={{base: "250px", xl: "400px"}} 
                                    onClick={()=>{
                                        window.open(`https://${eventShort}.groovestats.com/index.php?page=tourneymarlist&division=Lower`, '_blank').focus();
                                    }}    
                                >
                                    <Icon as={FaMusic} boxSize={5} mr={2}/> Marathon Data <Icon as={FiExternalLink} mx='2px' ml={2} top='1px' position="relative"/>
                                </Button>    
                            </Flex>
                            <Heading size="md">Upper Division:</Heading>
                            <Divider mb={4}/>
                            <Flex direction="row" wrap="wrap" alignSelf="center" gap={2}>
                                <Button 
                                    ml="auto" 
                                    mr="auto" 
                                    mb={4} 
                                    width={{base: "250px", xl: "400px"}} 
                                    onClick={()=>{
                                        window.open(`https://${eventShort}.groovestats.com/index.php?page=tourneysonglist&division=Upper`, '_blank').focus();
                                    }}    
                                >
                                    <Icon as={FaMusic} boxSize={5} mr={2}/> Raw Output Data <Icon as={FiExternalLink} mx='2px' ml={2} top='1px' position="relative"/>
                                </Button>
                                <Button 
                                    ml="auto" 
                                    mr="auto" 
                                    mb={4} 
                                    width={{base: "250px", xl: "400px"}} 
                                    onClick={()=>{
                                        window.open(`https://${eventShort}.groovestats.com/index.php?page=tourneymarlist&division=Upper`, '_blank').focus();
                                    }}    
                                >
                                    <Icon as={FaMusic} boxSize={5} mr={2}/> Marathon Data <Icon as={FiExternalLink} mx='2px' ml={2} top='1px' position="relative"/>
                                </Button>   
                            </Flex>                                                      
                        </Flex>                                                    
                        <Text mb={4}>
                            Keep reading below to learn more about set scheduling and the machine setups that will be available!
                        </Text>                                                                      
                    </Card>
                    <Card bgColor="whiteAlpha.300" flex="2" flexBasis="375px" padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>
                            Side Events:
                        </Heading>
                        <Divider mb={4}/>                            
                        <UnorderedList ml={isMobile() ? 8 : 12}>
                            <ListItem>
                                <Heading color="blue.200" size="xs">Stamina Trivia</Heading> 
                                <Text mb={2}>
                                    Your team can test your knowledge on stamina history, charts, and more by competing against
                                    other teams!
                                </Text>
                                <Text mb={2}>
                                    Teams may enter one of up to two separate sessions (depending on scheduling, each with different 
                                    questions) that will be hosted during {liveLong}.
                                </Text>      
                                <Flex fontSize="12px" flexWrap="wrap" ml="auto" mr="auto" alignContent="center" justifyContent="center" mb={2}>
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">1 Point</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Participating in a session</ListItem>                                 
                                        </UnorderedList>
                                    </Card> 
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">2 Points</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Second place for session</ListItem>                                     
                                        </UnorderedList>
                                    </Card>                                           
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">3 Points</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>First place for session</ListItem>
                                        </UnorderedList>
                                    </Card>                                                                                                
                                </Flex>                                                          
                            </ListItem>
                            <ListItem>
                                <Heading color="blue.200" size="xs">Sticker Hunt</Heading> 
                                <Text mb={2}>
                                    Stamina Jawn 2 stickers are hidden around Squirrel Hill!  How many can you find?
                                </Text>
                                <Text mb={2}>
                                    Snap a selfie with as many as you are able to get some points for your team!
                                </Text>
                                <Flex fontSize="12px" flexWrap="wrap" ml="auto" mr="auto" alignContent="center" justifyContent="center" mb={2}>
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">1 Point</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Finding at least one sticker</ListItem>                                 
                                        </UnorderedList>
                                    </Card> 
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">2 Points</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Finding four stickers</ListItem>                                     
                                        </UnorderedList>
                                    </Card>                                           
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">3 Points</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Team(s) with most stickers found</ListItem>
                                        </UnorderedList>
                                    </Card>                                                                                                
                                </Flex>                                 
                            </ListItem>                            
                            <ListItem>
                                <Heading color="blue.200" size="xs">Silk Song Challenge</Heading> 
                                <Text mb={2}>
                                    East Coast Stamina {eventNum} is themed after Silk Song, so it's only fitting to have a challenge
                                    related to it!
                                </Text>
                                <Text mb={2}>
                                    From the bench before the area starts, race to the surface in Silk Song.  You'll have 30 minutes to 
                                    try to complete this platforming speedrun.
                                </Text>
                                <Text mt={2} mb={2}>
                                    <strong>NOTE:</strong> You will need to pick only ONE team member to perform this challenge, and they
                                    may not be the same one that did the Archi or Xiwangz challenges, unless it's a two person team.  Two 
                                    person teams may have one team member do TWO of these three challenges.
                                </Text>
                                <Flex fontSize="12px" flexWrap="wrap" ml="auto" mr="auto" alignContent="center" justifyContent="center" mb={2}>
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">1 Point</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Attempting at all</ListItem>                                 
                                        </UnorderedList>
                                    </Card> 
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">2 Points</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Completing within 30 minutes</ListItem>                                     
                                        </UnorderedList>
                                    </Card>                                           
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">3 Points</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Completing within 15 minutes</ListItem>
                                        </UnorderedList>
                                    </Card>                                                                                                
                                </Flex>                                    
                            </ListItem>                 
                            <ListItem>
                                <Heading color="blue.200" size="xs">Archi's Challenge</Heading> 
                                <Text mb={2}>
                                    Think you can beat the Godfather of Stamina?  Well, choose from a list of games and try your luck!
                                    You'll get points based off of your performance.
                                </Text>
                                <Text mt={2} mb={2}>
                                    <strong>NOTE:</strong> You will need to pick only ONE team member to perform this challenge, and they
                                    may not be the same one that did the Silk Song or Xiwangz challenges, unless it's a two person team.  Two 
                                    person teams may have one team member do TWO of these three challenges.
                                </Text>
                                <Flex fontSize="12px" flexWrap="wrap" ml="auto" mr="auto" alignContent="center" justifyContent="center" mb={2}>
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">1 Point</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Attempting at all</ListItem>                                 
                                        </UnorderedList>
                                    </Card> 
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">2 Points</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Tying with Archi</ListItem>                                     
                                        </UnorderedList>
                                    </Card>                                           
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">3 Points</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Beating Archi</ListItem>
                                        </UnorderedList>
                                    </Card>                                                                                                
                                </Flex>
                            </ListItem>
                            <ListItem>
                                <Heading color="blue.200" size="xs">Xiwangz's Challenge</Heading> 
                                <Text mb={2}>
                                    Think you can beat our other event host?  You can pick from another list of games and try your luck!
                                    You'll get points based off of your performance.
                                </Text>
                                <Text mt={2} mb={2}>
                                    <strong>NOTE:</strong> You will need to pick only ONE team member to perform this challenge, and they
                                    may not be the same one that did the Silk Song or Archi challenges, unless it's a two person team.  Two 
                                    person teams may have one team member do TWO of these three challenges.
                                </Text>
                                <Flex fontSize="12px" flexWrap="wrap" ml="auto" mr="auto" alignContent="center" justifyContent="center" mb={2}>
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">1 Point</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Attempting at all</ListItem>                                 
                                        </UnorderedList>
                                    </Card> 
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">2 Points</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Tying with Xiwangz</ListItem>                                     
                                        </UnorderedList>
                                    </Card>                                           
                                    <Card bgColor="teal.600" width="190px" margin={1} padding={5}>
                                        <Heading size="sm" mr="auto" mb={2} ml="auto">3 Points</Heading>
                                        <UnorderedList ml={isMobile() ? 4 : 6}>
                                            <ListItem>Beating Xiwangz</ListItem>
                                        </UnorderedList>
                                    </Card>                                                                                                
                                </Flex>
                            </ListItem>                                   
                            <ListItem>
                                <Heading color="blue.200" size="xs">Group Picture</Heading> 
                                <Text mb={2}>
                                    I'm just putting this one here so that people are aware it's happening!  No points or anything for this one.
                                </Text>
                            </ListItem>  
                        </UnorderedList>                        
                        <Text>                                    
                            Most of these you can participate in whenever there's a chance to, but the trivia sessions and group photo times 
                            are listed on the <Link color='teal.300' onClick={()=>{changePage("calendar")}}>schedule</Link>.
                        </Text>                            
                    </Card>                    
                </Card>                                       
                <Card flex="2" flexBasis="375px" padding={isMobile()? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        Tournament Setups and Scheduling
                    </Heading>
                    <Divider mb={4}/>                
                    <Text mb={4}>
                        At the event, if you're competing, your team has the opportunity to select a setup to do your set(s) on. These mostly 
                        include DDR/ITG pads, but there are two dedicated Pump setups, and if you select a setup where you're allowed to swap 
                        the pads out, you can bring whatever full sized pad you want, regardless of whether it's ITG's panel configuration 
                        or Pump's.
                    </Text>
                    <Text mb={4}>
                        For <strong>travel/mini pads only</strong> (please check with us on 
                        <Link color='teal.300' href={'https://staminanation.com/'} isExternal> Discord <Icon as={FiExternalLink} mx='2px' top='3px' position="relative"/></Link> 
                        to make sure yours counts), you may bring and use the pad on <em>any</em> setup-- including the 'non-swappable' ones--
                        provided you bring a non-slip mat to place between your pad and the surface of the setup's default pad to avoid 
                        any damage.
                    </Text>
                    <Text mb={4}>
                        Note that after you've checked in at the event, <strong>scores obtained at any time on any setup will count towards your total</strong>.
                        However, during your set(s), you have guaranteed access to the setup you chose, so it is to your advantage to schedule one.
                    </Text>
                    <Text mb={4}>
                        Here are details on the different options for setups:
                    </Text>
                    <Flex mt={3} flexDirection="row" flexFlow="wrap" justifyContent={"center"}>
                        {SetupGrid}    
                    </Flex>
                    <Text mb={4}>
                        All entrants are allowed a <strong>5 hour block</strong> on the schedule for their Raw Output sets (one hour of pad modding/warming up + 4 hours for the set).  You may also schedule a <strong>2.5 hour block</strong> to 
                        attempt the full marathon (one hour of pad modding + 1.5 hours for the full marathon attempt).
                    </Text>                    
                    <Text mb={4}>
                        Outside of the core time slots for Raw Output and Marathon attempts, the machines will be free 
                        to use for anyone who wants to play, with priority given to players who have marathon splits 
                        they want to attempt.
                    </Text>
                    <Text mb={4}>
                        Solo teams won't be allowed to register for a place on the schedule.
                    </Text>
                    <Text mb={4}>
                        You can see what times the various entrants are playing by checking the calendar below:
                    </Text>
                    <Box textAlign="center">
                        <Button width={{base: "250px", xl: "600px"}} onClick={()=>{changePage("calendar")}}>
                            <Icon as={FaRegCalendar} boxSize={5} mr={2}/> Set Calendar/Schedule
                        </Button>
                    </Box>
                </Card>
                <SignUpDisplay/>
                <Card padding={isMobile() ? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        Who/Why is {liveLong}?
                    </Heading>
                    <Divider mb={4}/>
                    <Box alignContent="space-around">
                        <Heading align="center" size="md" mb={4}>
                            Currently,
                        </Heading>
                        <Heading align="center" size="xl" mb={4}>{regTotal} / {eventConfig["maxPlayerCount"]}</Heading>
                        <Heading align="center" size="md" mb={4}>
                            registration slots have been filled.
                        </Heading>
                    </Box>                    
                    <Text mb={4}>
                        I want to give special mention to the following people for contributing to this event and helping to make it possible:
                    </Text>
                    <UnorderedList ml={isMobile() ? 8 : 12}>
                        <ListItem><strong>The PGH Dance Game Space Staff - </strong> for allowing us to return this year and host more events!</ListItem>
                        <ListItem><strong>Xiwangz - </strong> for helping me plan and run the event!</ListItem>
                        <ListItem><strong>quietly-turning - </strong> for building the PGH Dance Game Space originally, and helping us get started running events here last year!  And a million other things!</ListItem>
                        <ListItem><strong>Corvaglia - </strong> for the logo design!</ListItem>                       
                        <ListItem>... and <strong>everyone who registered to come - </strong> we can't run an event like this if people don't want to attend, so thank you for being a part of everything!</ListItem>
                    </UnorderedList>
                </Card>
                <Card padding={isMobile() ? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>
                        Contributing
                    </Heading>     
                    <Divider mb={4}/>                                           
                    <DonationDisplay/>
                </Card>

            </Box>
        );
    } catch (e) {
        console.error("EventInfo render error: ", e);
    }
}